import * as React from "react";

import { toast } from "@replo/design-system/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { TRPC_ERROR_CODES_BY_KEY } from "@trpc/server/unstable-core-do-not-import";
import { WorkspaceDashboardContext } from "@v2/contexts/WorkspaceDashboardContext";
import { initWorkspaceBasedAnalytics } from "@v2/features/infra/analytics/init";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { subscriptionRouter } from "@v2/trpc/routes/subscription";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { handleTRPCClientError } from "@v2/utils/error-handling";
import { routes } from "@v2/utils/router";
import { Copy, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DebugCommandMenuWorkspaceItemsProvider = () => {
  const isDebugMode = useIsDebugMode();
  const workspaceContext = React.useContext(WorkspaceDashboardContext);

  // Open Workspace in Admin
  const openWorkspaceInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !workspaceContext?.workspace) {
      return null;
    }

    return {
      label: "Open Workspace in Admin",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: ExternalLink },
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/workspaces?workspaceId=${workspaceContext?.workspace?.id}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, workspaceContext?.workspace]);
  useDynamicCommandMenuItem(
    "debug-open-workspace-admin",
    openWorkspaceInAdminItem,
  );

  // Copy Workspace ID
  const copyWorkspaceIdItem = React.useMemo(() => {
    if (!isDebugMode || !workspaceContext?.workspace) {
      return null;
    }

    return {
      label: "Copy Workspace ID",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(workspaceContext.workspace!.id);
      },
    };
  }, [isDebugMode, workspaceContext?.workspace]);
  useDynamicCommandMenuItem("debug-copy-workspace-id", copyWorkspaceIdItem);

  return null;
};

const WorkspaceProvider: React.FC<
  React.PropsWithChildren<{
    workspaceId?: string;
    saveWorkspaceIdAsLastVisited?: boolean;
    showToastOnNoAccess?: boolean;
    redirectOnNoAccess?: boolean;
  }>
> = ({
  workspaceId,
  children,
  saveWorkspaceIdAsLastVisited = false,
  showToastOnNoAccess = false,
  redirectOnNoAccess = false,
}) => {
  const {
    data: workspaceData,
    isPending: isLoadingWorkspace,
    error: workspaceError,
  } = workspaceRouter.getById.useQuery(
    workspaceId ? { id: workspaceId } : skipToken,
    {
      // Note (Noah, 2025-12-22): We ignore default error handling for workspace access errors, since the user may be a
      // single project guest and thus does not have access to this endpoint
      meta: { reploIgnoreDefaultErrorHandling: true },
      retry: (failureCount, error) => {
        // Note (Noah, 2025-02-26): Don't retry workspace access errors, they'll
        // just fail again
        if (error.shape?.code === TRPC_ERROR_CODES_BY_KEY.FORBIDDEN) {
          return false;
        }
        return failureCount < 3;
      },
    },
  );

  const { data: subscriptionDetails } =
    subscriptionRouter.getActiveSubscription.useQuery(
      workspaceId
        ? {
            workspaceId,
          }
        : skipToken,

      {
        // Note (Noah, 2025-12-22): We ignore default error handling for workspace access errors, since the user may be a
        // single project guest and thus does not have access to this endpoint
        meta: { reploIgnoreDefaultErrorHandling: true },
      },
    );

  const navigate = useNavigate();

  React.useEffect(() => {
    if (
      workspaceError &&
      workspaceError instanceof TRPCClientError &&
      workspaceError.shape?.code === TRPC_ERROR_CODES_BY_KEY.FORBIDDEN
    ) {
      if (showToastOnNoAccess) {
        toast.error({
          title: "No access to workspace",
          description:
            "You don't have access to the requested workspace. Please reach out to support@replo.app with questions.",
        });
      }
      if (redirectOnNoAccess) {
        navigate(routes.allProjects);
      }
    } else if (workspaceError) {
      handleTRPCClientError(workspaceError);
    }
  }, [workspaceError, showToastOnNoAccess, redirectOnNoAccess, navigate]);

  // Update last visited workspace id in localstorage when needed
  React.useEffect(() => {
    // Note (Noah, 2025-02-26): If the workspace id wasn't specified or we weren't able
    // to load it, then don't save it as the last visited workspace since it was just
    // requested, not visited (this is e.g. the case if you have a single-project
    // guest account try to access the workspace via url).
    if (!workspaceId || !saveWorkspaceIdAsLastVisited || !workspaceData) {
      return;
    }
    localStorage.setItem("replo.workspace.lastvisitedWorkspaceId", workspaceId);
  }, [workspaceId, saveWorkspaceIdAsLastVisited, workspaceData]);

  // NOTE (Ben, 2024-08-16): We initialize workspace analytics here because all
  // workspace-based components (tabs for analytics + settings, etc) are
  // used as interior components.
  React.useEffect(() => {
    if (workspaceData) {
      initWorkspaceBasedAnalytics({
        id: workspaceData.workspace.id,
        name: workspaceData.workspace.name,
        subscriptionInfo: subscriptionDetails ?? undefined,
      });
    }
  }, [workspaceData, subscriptionDetails]);

  return (
    <WorkspaceDashboardContext.Provider
      value={{
        workspaceId: workspaceId ?? null,
        workspace: workspaceData?.workspace ?? null,
        isLoading: isLoadingWorkspace || !Boolean(workspaceId),
      }}
    >
      {children}
      <DebugCommandMenuWorkspaceItemsProvider />
    </WorkspaceDashboardContext.Provider>
  );
};

export default WorkspaceProvider;
